import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
  Chip,
} from '@mui/material';
import {
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Review, ReviewCreateInput, ReviewUpdateInput, ReviewSummary } from '../services/reviewsService';
import { useConfirm } from '../contexts/ConfirmDialogContext';

// Helper function to convert recommendation string to number
const getRecommendationValue = (recommendation: string | number): number => {
  if (typeof recommendation === 'number') return recommendation;
  
  switch (recommendation) {
    case '1':
    case 'no':
      return 1; // No
    case '2':
    case 'probably not':
      return 2; // Probably not
    case '3':
    case 'maybe':
      return 3; // Maybe
    case '4':
    case 'probably yes':
      return 4; // Probably yes
    case '5':
    case 'yes':
      return 5; // Yes, definitely
    default:
      return 3; // Default to maybe
  }
};

// Helper function to get recommendation label from number
const getRecommendationLabel = (value: number): string => {
  switch (value) {
    case 5:
      return 'Yes, definitely';
    case 4:
      return 'Probably yes';
    case 3:
      return 'Maybe';
    case 2:
      return 'Probably not';
    case 1:
      return 'No';
    default:
      return 'Maybe';
  }
};

interface ReviewSystemProps {
  reviews: Review[];
  summary: ReviewSummary | null;
  loading: boolean;
  error: string | null;
  cases: any[];
  clients: any[];
  onCreateReview: (data: ReviewCreateInput) => void;
  onUpdateReview: (id: number, data: ReviewUpdateInput) => void;
  onDeleteReview: (id: number) => void;
  organizationView?: boolean;
  lawyerId?: string;
  clientId?: number;
}

const ReviewSystem: React.FC<ReviewSystemProps> = ({
  reviews,
  summary,
  loading,
  error,
  cases,
  clients,
  onCreateReview,
  onUpdateReview,
  onDeleteReview,
  organizationView = false,
  lawyerId,
  clientId
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState<ReviewCreateInput>({
    user_id: '',
    case_id: 0,
    lawyer_satisfaction: 5,
    firm_satisfaction: 5,
    recommendation: 1,
  });

  const handleOpenDialog = (review?: Review) => {
    if (review) {
      setSelectedReview(review);
      setFormData({
        user_id: review.user_id || '',
        case_id: review.case_id || 0,
        lawyer_satisfaction: review?.lawyer_satisfaction,
        firm_satisfaction: review.firm_satisfaction,
        recommendation: review.recommendation,
      });
    } else {
      setSelectedReview(null);
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReview(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      case_id: 0,
      lawyer_satisfaction: 5,
      firm_satisfaction: 5,
      recommendation: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      if (selectedReview) {
        onUpdateReview(selectedReview.id, { ...formData, id: selectedReview.id });
        setSnackbar({ open: true, message: t('reviews.reviewUpdated'), severity: 'success' });
      } else {
        onCreateReview(formData);
        setSnackbar({ open: true, message: t('reviews.reviewCreated'), severity: 'success' });
      }
      handleCloseDialog();
    } catch (error) {
      setSnackbar({ open: true, message: t('reviews.failedToSaveReview'), severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm(t('reviews.confirmDeleteReview'));
    if (!ok) return;

    try {
      onDeleteReview(id);
      setSnackbar({ open: true, message: t('reviews.reviewDeleted'), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('reviews.failedToDeleteReview'), severity: 'error' });
    }
  };

  const renderStars = (rating: number, size: 'small' | 'medium' | 'large' = 'small') => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Rating value={rating} precision={0.1} readOnly size={size} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          {rating?.toFixed(1)}
        </Typography>
      </Box>
    );
  };

  const getCaseTitle = (caseId: number) => {
    const case_ = cases.find(c => c.id === caseId);
    return case_ ? case_.subject : t('reviews.unknownCase');
  };

  const getClientName = (clientId: number) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : t('cases.unknownClient');
  };

  // Filter reviews based on props (currently no filtering as Review type doesn't have lawyer_id/client_id)
  const filteredReviews = Array.isArray(reviews) ? reviews : []; // Ensure it's always an array

  return (
    <Box>
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      {summary && organizationView && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon />
            {t('reviews.reviewStatistics')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1, textAlign: 'center', mb: 2 }}>
                <Typography variant="h3" color="primary">
                  {summary.total_reviews || 0}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {t('reviews.totalReviews')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('reviews.positiveRecommendations', { rate: summary.recommendation_rate || 0 })}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('reviews.ratingDistribution')}</Typography>
                {[5, 4, 3, 2, 1]?.map((star) => (
                  <Box key={star} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 20 }}>{star}</Typography>
                      <StarIcon sx={{ fontSize: 16, color: '#faaf00' }} />
                      <LinearProgress 
                        variant="determinate" 
                        value={summary.total_reviews > 0 ? (summary.distribution?.[star] || 0) / summary.total_reviews * 100 : 0} 
                        sx={{ flex: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 30 }}>
                        {summary.distribution?.[star] || 0}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 2 }}>{t('reviews.categoryRatings')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="body2" color="text.secondary">{t('reviews.lawyerSatisfaction')}</Typography>
                {renderStars(summary.avg_lawyer_satisfaction || 0)}
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="body2" color="text.secondary">{t('reviews.firmSatisfaction')}</Typography>
                {renderStars(summary.avg_firm_satisfaction || 0)}
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="body2" color="text.secondary">{t('reviews.recommendation')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={t('reviews.percentPositive', { rate: summary.recommendation_rate || 0 })}
                    color={summary.recommendation_rate >= 80 ? 'success' : summary.recommendation_rate >= 60 ? 'warning' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {lawyerId ? t('reviews.lawyerReviews') : clientId ? t('reviews.clientReviews') : organizationView ? t('reviews.allReviews') : t('navigation.reviews')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
        >
          {t('reviews.writeReview')}
        </Button>
      </Box>

      {/* Reviews List */}
      {loading ? (
        <LinearProgress />
      ) : !filteredReviews || filteredReviews.length === 0 ? (
        <Alert severity="info">{t('reviews.noReviewsFoundWithCta')}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredReviews.map((review) => (
            <Card key={review.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar>
                      R
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">
                        {t('reviews.reviewNumber', { id: review.id })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getCaseTitle(review.case_id || 0)} • {new Date(review.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {renderStars(review?.lawyer_satisfaction)}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption" color="text.secondary">{t('reviews.lawyerSatisfaction')}</Typography>
                    {renderStars(review?.lawyer_satisfaction, 'small')}
                  </Box>
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption" color="text.secondary">{t('reviews.firmSatisfaction')}</Typography>
                    {renderStars(review?.firm_satisfaction, 'small')}
                  </Box>
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption" color="text.secondary">{t('reviews.recommendation')}</Typography>
                    {renderStars(review?.recommendation, 'small')}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Tooltip title={t('reviews.editReview')}>
                    <IconButton size="small" onClick={() => handleOpenDialog(review)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('reviews.deleteReview')}>
                    <IconButton size="small" color="error" onClick={() => handleDelete(review.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Create/Edit Review Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedReview ? t('reviews.editReview') : t('reviews.writeAReview')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label={t('billing.case')}
              fullWidth
              value={formData.case_id}
              onChange={(e) => setFormData({ ...formData, case_id: parseInt(e.target.value) })}
            >
              {cases?.map((case_) => (
                <MenuItem key={case_.id} value={case_.id}>
                  {case_.subject}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{t('reviews.lawyerSatisfaction')}</Typography>
                <Rating
                  value={formData.lawyer_satisfaction}
                  onChange={(_, newValue) => setFormData({ ...formData, lawyer_satisfaction: newValue || 5 })}
                  precision={0.5}
                />
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{t('reviews.firmSatisfaction')}</Typography>
                <Rating
                  value={formData.firm_satisfaction}
                  onChange={(_, newValue) => setFormData({ ...formData, firm_satisfaction: newValue || 5 })}
                  precision={0.5}
                />
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{t('reviews.recommendation')}</Typography>
                <TextField
                  select
                  fullWidth
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: parseInt(e.target.value) })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: getRecommendationValue(formData.recommendation) >= 4 ? 'success.light' : 
                                       getRecommendationValue(formData.recommendation) <= 2 ? 'error.light' : 'grey.100',
                      color: getRecommendationValue(formData.recommendation) >= 4 ? 'success.contrastText' : 
                             getRecommendationValue(formData.recommendation) <= 2 ? 'error.contrastText' : 'text.primary',
                    },
                  }}
                >
                  <MenuItem value="1">{t('reviews.no')}</MenuItem>
                  <MenuItem value="2">{t('reviews.probablyNot')}</MenuItem>
                  <MenuItem value="3">{t('reviews.maybe')}</MenuItem>
                  <MenuItem value="4">{t('reviews.probablyYes')}</MenuItem>
                  <MenuItem value="5">{t('reviews.yesDefinitely')}</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedReview ? t('reviews.updateReview') : t('reviews.submitReview')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewSystem;
